import React from 'react';
import styles from './PromptDiffViewer.module.css';
import Botao from '@/components/Botao';
import HBox from '@/components/layout/HBox';
import VBox from '@/components/layout/VBox';
import Icon from '@/components/Icon';
import ModalPromptPreview from './ModalPromptPreview';
import { mdiCheckboxBlankOutline, mdiCheckboxMarked, mdiAlertCircleOutline, mdiCheck, mdiCheckCircle, mdiCodeBraces } from '@mdi/js';

/** Extrai N linhas de contexto antes e depois de `textoOriginal` dentro de `fieldText` */
function extractContext(fieldText: string, textoOriginal: string, numCtx = 2) {
    if (!textoOriginal || !fieldText) return { ctxBefore: [] as string[], ctxAfter: [] as string[] };
    const idx = fieldText.indexOf(textoOriginal);
    if (idx === -1) return { ctxBefore: [] as string[], ctxAfter: [] as string[] };
    const allLines = fieldText.split('\n');
    const startLine = fieldText.substring(0, idx).split('\n').length - 1;
    const endLine = startLine + textoOriginal.split('\n').length - 1;
    return {
        ctxBefore: allLines.slice(Math.max(0, startLine - numCtx), startLine),
        ctxAfter: allLines.slice(endLine + 1, endLine + 1 + numCtx),
    };
}

/** Aplica sugestões selecionadas a um snapshot de regras */
function applySelected(
    snapshot: RegrasSnapshot,
    sugestoes: SugestaoDiff[],
    selected: Set<number>
): { pode_fazer: string; nao_pode_fazer: string } {
    let pf = snapshot.pode_fazer;
    let npf = snapshot.nao_pode_fazer;
    sugestoes.forEach((s, i) => {
        if (!selected.has(i)) return;
        if (s.campo === 'pode_fazer') {
            pf = s.texto_original ? pf.replace(s.texto_original, s.texto_novo) : pf + (s.texto_novo ? '\n' + s.texto_novo : '');
        } else {
            npf = s.texto_original ? npf.replace(s.texto_original, s.texto_novo) : npf + (s.texto_novo ? '\n' + s.texto_novo : '');
        }
    });
    return { pode_fazer: pf, nao_pode_fazer: npf };
}

type AnnotatedLine = { type: 'context' | 'removed' | 'added'; text: string };

/** Constrói linhas anotadas (context/removed/added) para um campo completo */
export function buildAnnotatedLines(
    fieldText: string,
    allSugestoes: SugestaoDiff[],
    selected: Set<number>,
    campo: 'pode_fazer' | 'nao_pode_fazer'
): AnnotatedLine[] {
    const applicable = allSugestoes
        .map((s, i) => ({ s, i }))
        .filter(({ s, i }) => s.campo === campo && selected.has(i) && !!s.texto_original)
        .sort((a, b) => fieldText.indexOf(a.s.texto_original) - fieldText.indexOf(b.s.texto_original));

    const pureAdds = allSugestoes
        .map((s, i) => ({ s, i }))
        .filter(({ s, i }) => s.campo === campo && selected.has(i) && !s.texto_original && !!s.texto_novo);

    const result: AnnotatedLine[] = [];
    let remaining = fieldText;

    for (const { s } of applicable) {
        const idx = remaining.indexOf(s.texto_original);
        if (idx === -1) continue;
        const before = remaining.substring(0, idx);
        if (before) before.split('\n').forEach((t) => result.push({ type: 'context', text: t }));
        s.texto_original.split('\n').forEach((t) => result.push({ type: 'removed', text: t }));
        if (s.texto_novo) s.texto_novo.split('\n').forEach((t) => result.push({ type: 'added', text: t }));
        remaining = remaining.substring(idx + s.texto_original.length);
    }

    if (remaining) remaining.split('\n').forEach((t) => result.push({ type: 'context', text: t }));
    for (const { s } of pureAdds) {
        s.texto_novo.split('\n').forEach((t) => result.push({ type: 'added', text: t }));
    }

    return result;
}

export type SugestaoDiff = {
    campo: 'pode_fazer' | 'nao_pode_fazer';
    texto_original: string;
    texto_novo: string;
    descricao: string;
};

export type RegrasSnapshot = {
    pode_fazer: string;
    nao_pode_fazer: string;
    hash: string;
};

type Props = {
    sugestoes: SugestaoDiff[];
    regrasSnapshot: RegrasSnapshot | null;
    hashAtual: string | null; // hash das regras atuais do agente (null = não carregado)
    loadingHashAtual?: boolean;
    selected: Set<number>;
    onToggle: (idx: number) => void;
    onSelectAll: () => void;
    onDeselectAll: () => void;
    onAplicar: () => void;
    applyLoading?: boolean;
    applyDone?: boolean;
};

const CAMPO_LABEL: Record<string, string> = {
    pode_fazer: 'O que pode fazer',
    nao_pode_fazer: 'O que não pode fazer',
};

/** Renderiza um bloco de diff estilo VSCode para uma sugestão */
const DiffBloco = ({
    sugestao,
    idx,
    selected,
    onToggle,
    fieldText,
}: {
    sugestao: SugestaoDiff;
    idx: number;
    selected: boolean;
    onToggle: () => void;
    fieldText?: string;
}) => {
    const isPureAdd = !sugestao.texto_original && !!sugestao.texto_novo;
    const isPureRemove = !!sugestao.texto_original && !sugestao.texto_novo;
    const isReplace = !!sugestao.texto_original && !!sugestao.texto_novo;

    // Linhas de contexto (2 antes e depois) extraídas do campo completo
    const { ctxBefore, ctxAfter } = fieldText
        ? extractContext(fieldText, sugestao.texto_original, 2)
        : { ctxBefore: [], ctxAfter: [] };

    // Para adição pura, usa as últimas 2 linhas do campo como contexto anterior
    const ctxBeforePureAdd: string[] = isPureAdd && fieldText
        ? fieldText.split('\n').slice(-2)
        : [];

    return (
        <div className={[styles.diffBloco, selected ? styles.diffBlocoSelected : ''].join(' ')}>
            <div className={styles.diffHeader} onClick={onToggle}>
                <Icon
                    path={selected ? mdiCheckboxMarked : mdiCheckboxBlankOutline}
                    size={0.9}
                    color={selected ? 'var(--tc-color-primary)' : 'var(--tc-color-gray-400)'}
                />
                <span className={styles.diffCampoTag}>{CAMPO_LABEL[sugestao.campo] ?? sugestao.campo}</span>
                <span className={styles.diffDescricao}>{sugestao.descricao}</span>
            </div>

            <div className={styles.diffLines}>
                {/* Contexto antes */}
                {(ctxBefore.length > 0 || ctxBeforePureAdd.length > 0) &&
                    (ctxBefore.length > 0 ? ctxBefore : ctxBeforePureAdd).map((line, i) => (
                        <div key={`ctx-b-${i}`} className={styles.diffLineContext}>
                            <span className={styles.diffLineSymbol}> </span>
                            <pre className={styles.diffLineText}>{line}</pre>
                        </div>
                    ))}
                {/* Linhas removidas */}
                {(isPureRemove || isReplace) &&
                    sugestao.texto_original.split('\n').map((line, i) => (
                        <div key={`rem-${i}`} className={styles.diffLineRemoved}>
                            <span className={styles.diffLineSymbol}>−</span>
                            <pre className={styles.diffLineText}>{line}</pre>
                        </div>
                    ))}
                {/* Linhas adicionadas */}
                {(isPureAdd || isReplace) &&
                    sugestao.texto_novo.split('\n').map((line, i) => (
                        <div key={`add-${i}`} className={styles.diffLineAdded}>
                            <span className={styles.diffLineSymbol}>+</span>
                            <pre className={styles.diffLineText}>{line}</pre>
                        </div>
                    ))}
                {/* Contexto depois */}
                {ctxAfter.length > 0 &&
                    ctxAfter.map((line, i) => (
                        <div key={`ctx-a-${i}`} className={styles.diffLineContext}>
                            <span className={styles.diffLineSymbol}> </span>
                            <pre className={styles.diffLineText}>{line}</pre>
                        </div>
                    ))}
            </div>
        </div>
    );
};

const PromptDiffViewer: React.FC<Props> = ({
    sugestoes,
    regrasSnapshot,
    hashAtual,
    loadingHashAtual,
    selected,
    onToggle,
    onSelectAll,
    onDeselectAll,
    onAplicar,
    applyLoading,
    applyDone,
}) => {
    const [showFullPrompt, setShowFullPrompt] = React.useState(false);

    if (!sugestoes.length) {
        return (
            <div className={styles.empty}>
                Nenhuma sugestão de alteração — o atendimento foi considerado adequado.
            </div>
        );
    }

    const hashDiverge =
        regrasSnapshot && hashAtual && !loadingHashAtual && hashAtual !== regrasSnapshot.hash;

    const allSelected = sugestoes.every((_, i) => selected.has(i));
    const noneSelected = sugestoes.every((_, i) => !selected.has(i));
    const totalLabel = sugestoes.length === 1 ? '1 sugestão' : `${sugestoes.length} sugestões`;
    const selLabel = selected.size === 1 ? '1 selecionada' : `${selected.size} selecionadas`;

    const fullPrompt = regrasSnapshot && selected.size > 0
        ? applySelected(regrasSnapshot, sugestoes, selected)
        : null;

    return (
        <VBox gap="12px">
            {hashDiverge && (
                <div className={styles.hashWarning}>
                    <Icon path={mdiAlertCircleOutline} size={0.85} />
                    <span>
                        As regras do agente foram alteradas após esta supervisão. Aplicar as sugestões
                        abaixo pode sobrescrever mudanças feitas posteriormente. Revise com atenção.
                    </span>
                </div>
            )}

            <HBox gap="8px" style={{ alignItems: 'center' }}>
                <span style={{ fontSize: 13, color: 'var(--tc-color-gray-600)', flex: 1 }}>
                    {totalLabel} · {selLabel}
                </span>
                <Botao variant="transparent" onClick={allSelected ? onDeselectAll : onSelectAll} style={{ fontSize: 12, padding: '2px 8px' }}>
                    {allSelected ? 'Desmarcar todas' : 'Selecionar todas'}
                </Botao>
            </HBox>

            {sugestoes.map((s, i) => (
                <DiffBloco
                    key={i}
                    sugestao={s}
                    idx={i}
                    selected={selected.has(i)}
                    onToggle={() => onToggle(i)}
                    fieldText={regrasSnapshot?.[s.campo]}
                />
            ))}

            <HBox gap="8px">
                {fullPrompt && (
                    <Botao
                        variant="outline-secondary"
                        onClick={() => setShowFullPrompt(true)}
                        title="Pré-visualizar o campo completo com as alterações aplicadas"
                    >
                        <HBox gap="6px">
                            <Icon path={mdiCodeBraces} size={0.8} />
                            Ver prompt ajustado
                        </HBox>
                    </Botao>
                )}
                <Botao
                    variant={applyDone ? 'success' : 'primary'}
                    disabled={noneSelected || applyLoading || applyDone}
                    onClick={onAplicar}
                >
                    {applyLoading ? (
                        'Aplicando...'
                    ) : applyDone ? (
                        <HBox gap="6px">
                            <Icon path={mdiCheckCircle} size={0.8} />
                            Aplicado
                        </HBox>
                    ) : (
                        <HBox gap="6px">
                            <Icon path={mdiCheck} size={0.8} />
                            Aplicar selecionadas no agente
                        </HBox>
                    )}
                </Botao>
            </HBox>

            {regrasSnapshot && (
                <ModalPromptPreview
                    show={showFullPrompt}
                    onClose={() => setShowFullPrompt(false)}
                    regrasSnapshot={regrasSnapshot}
                    sugestoes={sugestoes}
                    selected={selected}
                    onAplicar={onAplicar}
                    applyLoading={applyLoading}
                    applyDone={applyDone}
                />
            )}
        </VBox>
    );
};

export default PromptDiffViewer;
